import React, { ComponentProps, useEffect, useState } from "react"
import cx from "classnames"
import Modal from "../Modal"
import useBreakpoint from "../../hooks/useBreakpoint"
import { CloseIcon } from "../Icon"
import ReactDOM from "react-dom"
import { Fixed } from "../Layout/Surfaces"

type SidepanelFooterPropsType = React.PropsWithChildren<{
  className?: string
}>

const SidepanelFooter = ({ children, className }: SidepanelFooterPropsType) => {
  return (
    <div className={cx("c-p-sm c-border-t c-border-outline", className)}>
      {children}
    </div>
  )
}

type SidepanelPropsType = React.PropsWithChildren<{
  isOpen: boolean
  title?: string
  close?: () => void
  width?: ComponentProps<typeof Fixed>["width"]
  withDesktopOverlay?: boolean
}>

// Memoized component to not render content when the panel is collapsing
function _SidepanelContent({ title, close, children }: SidepanelPropsType) {
  return (
    <>
      {title && (
        <div className="c-flex c-gap-sm c-items-center c-px-sm c-py-md c-border-b c-border-outline">
          <div className="c-text-title-md c-flex-1">{title}</div>
          {close && (
            <div className="c-w-md c-cursor-pointer" onClick={close}>
              <CloseIcon size={24} />
            </div>
          )}
        </div>
      )}
      {children}
    </>
  )
}

function areSidepanelContentEqual(
  _prevProps: SidepanelPropsType,
  nextProps: SidepanelPropsType
) {
  return !nextProps.isOpen
}

const SidepanelContent = React.memo(_SidepanelContent, areSidepanelContentEqual)
SidepanelContent.displayName = "SidepanelContent"

// Only for the API, render nothing
const SidepanelFooterAPI = (_props: SidepanelFooterPropsType) => null

const isSidepanelFooterAPIComponent = (
  component: React.ReactNode
): component is React.ReactElement<SidepanelFooterPropsType> =>
  React.isValidElement(component) && component.type === SidepanelFooterAPI

const _Sidepanel = ({
  isOpen,
  title,
  close,
  withDesktopOverlay,
  width = 420,
  children,
}: SidepanelPropsType) => {
  const { isMobile } = useBreakpoint()

  // To display box-shadow when visible. We can't rely on isOpen because it triggers the collapse animation and so the box shadow would not be set during the transition
  const [isPanelVisible, setIsPanelVisible] = useState(false)

  useEffect(() => {
    isOpen && setIsPanelVisible(true)
  }, [isOpen])

  const sidepanelFooter = React.Children.toArray(children).find((c) =>
    isSidepanelFooterAPIComponent(c)
  )

  let footer: React.ReactNode = null
  if (React.isValidElement(sidepanelFooter)) {
    footer = isMobile ? (
      <Modal.Footer {...sidepanelFooter.props}></Modal.Footer>
    ) : (
      <SidepanelFooter {...sidepanelFooter.props} />
    )
  }

  return isMobile ? (
    <Modal
      aria-label={"Sidepanel"}
      isOpen={isOpen}
      bodySpacing={false}
      close={close}
      title={title}
    >
      {children}
      {footer}
    </Modal>
  ) : (
    ReactDOM.createPortal(
      <>
        <div
          className={cx("cobalt-sidepanel", {
            "cobalt-sidepanel--show": isOpen,
            "cobalt-sidepanel--visible": isPanelVisible,
          })}
          onTransitionEnd={() => {
            !isOpen && setIsPanelVisible(false)
          }}
        >
          <Fixed width={width} isFullHeight>
            <SidepanelContent isOpen={isOpen} title={title} close={close}>
              {children}
              {footer}
            </SidepanelContent>
          </Fixed>
        </div>
        {withDesktopOverlay && <div className="cobalt-sidepanel-overlay"></div>}
      </>,
      document.body
    )
  )
}

_Sidepanel.displayName = "Sidepanel"

export const Sidepanel = Object.assign(_Sidepanel, {
  Footer: SidepanelFooterAPI,
})
